/**
 * Auth Initializer Hook (Application)
 *
 * Runs authentication state initialization on mount.
 * Uses auth and user application/infrastructure; consumed by AuthInitializer (presentation).
 */

import { useEffect, useRef } from 'react';

import type { User } from '@/modules/user';
import { getCurrentUser } from '@/modules/user';

import { AuthStatus } from '../../domain/types';
import { authRepository } from '../../infrastructure/repositories';
import { tokenStorage } from '../../infrastructure/services';
import { useAuthStore } from '../store';

/**
 * Hook that initializes auth state on mount.
 * Call once at app root (e.g. in AuthInitializer).
 */
export function useAuthInitializer(): void {
  const initRef = useRef(false);
  const setUser = useAuthStore(state => state.setUser);
  const setStatus = useAuthStore(state => state.setStatus);
  const setInitialized = useAuthStore(state => state.setInitialized);
  const clearAuth = useAuthStore(state => state.clearAuth);

  useEffect(() => {
    if (initRef.current) return;
    initRef.current = true;

    const initializeAuth = async () => {
      const accessToken = tokenStorage.getAccessToken();
      const cachedUser = tokenStorage.getUserData<User>();

      if (!accessToken) {
        setStatus(AuthStatus.Unauthenticated);
        setInitialized();
        return;
      }

      if (tokenStorage.isTokenExpired()) {
        // Attempt refresh — the refresh token cookie is sent automatically
        try {
          await authRepository.refreshToken();
          const { user } = await getCurrentUser();
          setUser(user);
          tokenStorage.setUserData(user);
          setInitialized();
          return;
        } catch {
          tokenStorage.clearAll();
          clearAuth();
          setInitialized();
          return;
        }
      }

      if (cachedUser) {
        setUser(cachedUser);
        setInitialized();
        getCurrentUser()
          .then(({ user }) => {
            setUser(user);
            tokenStorage.setUserData(user);
          })
          .catch(() => {});
        return;
      }

      try {
        const { user } = await getCurrentUser();
        setUser(user);
        tokenStorage.setUserData(user);
        setInitialized();
      } catch {
        tokenStorage.clearAll();
        clearAuth();
        setInitialized();
      }
    };

    initializeAuth();
  }, [setUser, setStatus, setInitialized, clearAuth]);
}
